import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Box, Button, Checkbox, Grid, TextField } from "@mui/material";
import LinkedServices from "../../Resource/LinkedServices";
import { isArray } from "lodash";
import { useResourceContext } from "../../../contexts/ResourceContext";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ConfirmationDialog from "../../Dialogs/ConfirmationDialog";
import {
  aggregatedValidateSchema,
  reRunLocalLinkedServiceSchema,
  reRunBlobLinkedServiceSchema,
  reRunSftpLinkedServiceSchema,
  reRunSqlLinkedServiceSchema,
  reRunBasicAuthApiDefinitionSchema,
  reRunKeyAuthApiDefinitionSchema,
  reRunTokenAuthApiDefinitionSchema,
  reRunOAuthApiDefinitionSchema,
  reRunNoAuthApiDefinitionSchema,
} from "../../../schemas";
import { apiType, sqlDatabaseType } from "../../../services/constants";
import { removeUndefinedProperties } from "../../../services/utils";
import { updateResource } from "../../../services/resourcesService";
import { toast } from "react-toastify";
import useFetchResourceColumnsById from "../../../hooks/useFetchResourceColumnsById";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import { useToast } from "../../../services/utils";
import ReRunLinkedServices from "../../Resource/ReRun/ReRunLinkedServices";
import useFetchResourceByResourceId from "../../../hooks/useFetchResourceByResourceId";
import { useAuth } from "../../../contexts/AuthContext";

interface IResourceData {
  resourceData: any;
  resourcesData: any;
  // setIsEditResource: Dispatch<SetStateAction<boolean>>;
  setResourcesData: Dispatch<SetStateAction<any>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  linkedServicesData: any;
  connectionKeysData: any;
  // allResourcesData: any;
  setSelectSystemId?: Dispatch<SetStateAction<any>>; // Added for system field support
  isAggregatedBaseResource: boolean;
  aggregatedResourceId: any;
  fileStreamIndex?: number;
  isAdditionalResource?: boolean;
  resource_id: number | null | undefined;
  isFromRuleExecution: boolean;
  isFromQueryExecution?: boolean;
}

const ReRunEditRuleExecutionTab = ({
  resourcesData,
  resourceData,
  setIsLoading,
  setResourcesData,
  linkedServicesData,
  connectionKeysData,
  setSelectSystemId,
  isAggregatedBaseResource,
  aggregatedResourceId,
  fileStreamIndex,
  isAdditionalResource,
  resource_id,
  isFromRuleExecution,
  isFromQueryExecution = false,
}: IResourceData) => {
  const {
    globalVariables,
    setGlobalVariables,
    setIsResourceEdit,
    viewInlineVariables,
    setViewInlineVariables,
    fetchedAllConnectionKeys,
  } = useRuleResourceContext();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canEditResource = hasPermission("resource", "edit");
  const [formData, setFormData] = useState<any>({
    aggregation_type: "flat",
  });
  const [resourceId, setResourceId] = useState<any>(0);
  const [openResourceConfirmation, setOpenResourceConfirmation] =
    useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [queryParams, setQueryParams] = useState([]);
  const [urlParams, setUrlParams] = useState([]);
  const [type, setType] = useState("");
  const [allVariablesList, setAllVariablesList] = useState<any>({});

  const [currentResourceColumnId, setCurrentResourceColumnId] =
    useState<any>(null);
  const [cancelUploadFile, setCancelUploadFile] = useState<any>(false);
  const [isLoadingFile, setIsLoadingFile] = useState<boolean>(false);
  const [comment, setComment] = useState("");
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [initialBaseResourceColumns, setInitialBaseResourceColumns] =
    useState<any>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [hasVariableChanges, setHasVariableChanges] = useState<boolean>(false);
  const [authTagDetails, setAuthTagDetails] = useState([]);
  const [currentResourceId, setCurrentResourceId] = useState(null);

  const [resourceColumnData] = useFetchResourceColumnsById({
    resourceColumnId: currentResourceColumnId,
    setIsLoading,
  });
  const [resourceDetailData] = useFetchResourceByResourceId({
    currentResourceId,
    setIsLoading,
  });

  const { baseResourceColumns } = useResourceContext();

  /**
   * Helper function to resolve connection key using dual-lookup pattern
   * Matches the pattern from ViewResourceData.tsx
   * Method 1: Direct connection_key (normal execution)
   * Method 2: Through linked_service_id (ReRun execution)
   */
  const resolveConnectionKey = (resourceDataParam: any): any => {
    const allAvailableConnectionKeys = [
      ...(connectionKeysData || []),
      ...(fetchedAllConnectionKeys || []),
    ];

    let connectionKey: any = null;

    // Method 1: Direct connection_key (normal execution)
    if (resourceDataParam?.connection_key) {
      connectionKey = allAvailableConnectionKeys.find(
        (ck: any) => ck.id === resourceDataParam.connection_key
      );
    }

    // Method 2: Through linked_service_id (ReRun execution)
    if (!connectionKey && resourceDataParam?.linked_service_id) {
      const linkedService = linkedServicesData?.find(
        (ls: any) => ls.id === resourceDataParam.linked_service_id
      );
      // Get connection key ID from linked service connection_details
      let connectionKeyId: any = null;
      if (linkedService?.connection_details?.connection_keys?.length > 0) {
        connectionKeyId = linkedService.connection_details.connection_keys[0];
      } else if (
        linkedService?.connection_details?.connection_keys_details?.length > 0
      ) {
        connectionKeyId =
          linkedService.connection_details.connection_keys_details[0]
            .connection_key_id;
      }
      if (connectionKeyId) {
        connectionKey = allAvailableConnectionKeys.find(
          (ck: any) => ck.id === connectionKeyId
        );
      }
    }

    return connectionKey;
  };

  /**
   * Helper function to resolve system_id using dual-lookup pattern
   * Finds the connection key first, then gets system_id from it
   */
  const resolveSystemId = (resourceDataParam: any): number | null => {
    const connectionKey = resolveConnectionKey(resourceDataParam);
    if (connectionKey?.system_id) {
      return connectionKey.system_id;
    }
    return null;
  };

  /**
   * Helper function to resolve connection_key ID using dual-lookup pattern
   * Returns the connection key ID that should be set in formData
   */
  const resolveConnectionKeyId = (resourceDataParam: any): number | null => {
    const connectionKey = resolveConnectionKey(resourceDataParam);
    if (connectionKey?.id) {
      return connectionKey.id;
    }
    return null;
  };

  useEffect(() => {
    if (resourceData) {
      setIsLoading(true);
      setResourceId(
        resourceData?.aggregation_properties?.base_resource_id ?? 0
      );
      setCurrentResourceColumnId(resourceData?.resource_column_details_id);
      const newFormData = {
        ...resourceData,
        file_processing_id: resourceData.file_processing_id,
        file_processing_code: resourceData.file_processing_code,
        system_id: resolveSystemId(resourceData),
        connection_key: resolveConnectionKeyId(resourceData),
        aggregation_type: resourceData?.aggregation_properties?.base_resource_id
          ? "aggregated"
          : "flat",
        base_resource_id:
          resourceData?.aggregation_properties?.base_resource_id,
        base_resource_code:
          resourceData?.aggregation_properties?.base_resource_code,
        resource_column_details_code:
          resourceData?.resource_column_details_code,
        base_resource_validation:
          resourceData?.aggregation_properties?.base_resource_validation,
        aggregation_query:
          resourceData?.aggregation_properties?.aggregation_query,
        base_resource_columns:
          resourceData?.aggregation_properties?.base_resource_columns,
        isUpdateResource: false,
      };
      const linkedData = linkedServicesData?.find(
        (item: any) => item.id === resourceData.linked_service_id
      );
      newFormData.linked_service = linkedData;
      // Set system_id from the linked service data
      //newFormData.system_id = linkedData?.system_id;
      // newFormData.additional_base_resource_data =
      //   resourceData?.additional_properties
      //     ?.additional_base_resource_data !== null
      //     ? resourceData?.additional_properties
      //         ?.additional_base_resource_data?.length > 0
      //       ? JSON.stringify(
      //           resourceData?.additional_properties
      //             ?.additional_base_resource_data
      //         )
      //       : []
      //     : null;
      // newFormData.additional_resource_data =
      //   resourceData?.additional_resource_data || [];
      setFormData(newFormData);

      setInitialBaseResourceColumns(
        resourceData?.aggregation_properties?.base_resource_columns || []
      );

      setQueryParams(resourceData?.api_definition?.query_params || {});
      setUrlParams(resourceData?.api_definition?.url_params || {});
      setInitialFormData(JSON.parse(JSON.stringify(newFormData)));

      if (connectionKeysData) {
        //connectionKeyDetail for type api only
        const connectionKeyDetail = connectionKeysData?.find(
          (option: { id: any }) =>
            option.id === resourceData?.api_definition?.connection_key
        );
        setType(connectionKeyDetail?.type || " ");
      }
      if (isFromQueryExecution) {
        setGlobalVariables(
          viewInlineVariables?.used_generic_research_query_variables
        );
        setAllVariablesList((prev: any) => ({
          ...prev,
          resource: {
            ...(viewInlineVariables?.used_generic_research_query_variables ??
              {}),
          },
          resourceColumn: {
            ...(viewInlineVariables?.used_generic_research_query_variables ??
              {}),
          },
        }));
      } else {
        setGlobalVariables(resourceData?.inline_variables ?? {});
        setAllVariablesList((prev: any) => ({
          ...prev,
          resource: {
            ...(resourceData?.inline_variables ?? {}),
          },
          resourceColumn: {
            ...(resourceData?.inline_variables ?? {}),
          },
        }));
      }
      setIsLoading(false);
    }
  }, [
    resourceData,
    linkedServicesData,
    connectionKeysData,
    fetchedAllConnectionKeys,
  ]);

  // Update hasChanges when formData or fileData changes
  useEffect(() => {
    const formChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);
    const fileProcessingIdChanged =
      JSON.stringify(formData?.file_processing_id) !==
      JSON.stringify(initialFormData?.file_processing_id);
    const baseResourceColumnsChanged =
      JSON.stringify(baseResourceColumns) !==
      JSON.stringify(initialBaseResourceColumns);

    const systemIdChanged =
      JSON.stringify(formData?.system_id) !==
      JSON.stringify(initialFormData?.system_id);

    setHasChanges(
      formChanged ||
        fileProcessingIdChanged ||
        baseResourceColumnsChanged ||
        systemIdChanged ||
        hasVariableChanges
    );
  }, [
    formData,
    baseResourceColumns,
    initialFormData,
    initialBaseResourceColumns,
    hasVariableChanges,
  ]);

  const onSaveResource = () => {
    const newResourceData: any = {};
    let uniqueBaseResourceColumns;
    if (baseResourceColumns && baseResourceColumns.length > 0) {
      const uniqueColumns = new Set(
        baseResourceColumns.map((column: { label: any }) => column.label)
      );
      uniqueBaseResourceColumns = Array.from(uniqueColumns);
    } else {
      uniqueBaseResourceColumns = null;
    }
    Object.assign(newResourceData, {
      ...formData,
      linked_service_id: formData?.linked_service?.id,
      linked_service_code: formData.linked_service?.code,
      is_active: true,
      file_processing_id: formData?.file_processing_id,
      file_processing_code: formData?.file_processing_code,
      api_definition: {
        ...formData?.api_definition,
        query_params: queryParams,
        url_params: urlParams,
        body:
          formData?.api_definition?.method !== "get"
            ? formData?.api_definition?.body
            : null,
      },
      resource_column_details_id:
        parseInt(formData.resource_column_details_id) || null,
      additional_base_resource_data: formData?.additional_base_resource_data,
      // additional_base_resource_data:
      //   formData?.additional_base_resource_data !== null
      //     ? formData?.additional_base_resource_data?.length > 0
      //       ? JSON.parse(formData?.additional_base_resource_data)
      //       : []
      //     : null,
      additional_resource_data: formData?.additional_resource_data || null,
      aggregation_properties: {
        base_resource_id: formData.base_resource_id || null,
        base_resource_code: formData.base_resource_code || null,
        aggregation_query: formData.aggregation_query || null,
        base_resource_columns: uniqueBaseResourceColumns,
        base_resource_validation: formData.base_resource_validation || false,
      },
      inline_variables: globalVariables,
      resource_column_details_code:
        formData?.resource_column_details_code || null,
    });
    let updatedResourcesData: any;
    if (isAggregatedBaseResource) {
      updatedResourcesData = resourcesData.map(
        (resource: { resource_id: any }) => {
          if (resource?.resource_id === aggregatedResourceId) {
            let res = {
              ...resource,
              aggregation_base_resource_data: newResourceData,
            };
            return res;
          }
          return resource;
        }
      );
      setResourcesData(updatedResourcesData);
    } else if (isAdditionalResource) {
      if (isArray(resourcesData)) {
        let updatedAdditionalResourceData: any;
        updatedResourcesData = resourcesData.map((resource) => {
          const isMatchingResource = resourceData?.isSecondaryResource
            ? resource?.resource_id === resource_id &&
              resource?.parentResId === resourceData?.parentResId
            : resource?.resource_id === resource_id;
          if (isMatchingResource) {
            if (resourceData?.isFromBaseResource) {
              updatedAdditionalResourceData =
                resource.aggregation_base_resource_data.additional_base_resource_data.map(
                  (additionalResource: { resource_id: any }) => {
                    if (
                      additionalResource.resource_id ===
                      resourceData?.resource_id
                    ) {
                      return newResourceData;
                    }
                    return additionalResource;
                  }
                );
            } else {
              updatedAdditionalResourceData =
                resource.additional_base_resource_data.map(
                  (additionalResource: { resource_id: any }) => {
                    if (
                      additionalResource.resource_id ===
                      resourceData?.resource_id
                    ) {
                      return newResourceData;
                    }
                    return additionalResource;
                  }
                );
            }
            return resourceData?.isFromBaseResource
              ? {
                  ...resource,
                  aggregation_base_resource_data: {
                    ...resource.aggregation_base_resource_data,
                    additional_base_resource_data:
                      updatedAdditionalResourceData,
                  },
                }
              : {
                  ...resource,
                  additional_base_resource_data: updatedAdditionalResourceData,
                };
          }
          return resource;
        });
        setResourcesData(updatedResourcesData);
      } else {
        updatedResourcesData = resourcesData?.additional_base_resource_data.map(
          (resource: { resource_id: any }) => {
            if (resource.resource_id === resourceData?.resource_id) {
              return newResourceData;
            }
            return resource;
          }
        );
        setResourcesData((prev: any) => ({
          ...prev,

          additional_base_resource_data: updatedResourcesData,
        }));
      }
    } else {
      const updatedResourcesData = resourcesData.map(
        (resource: {
          id: any;
          resource_id: any;
          isSecondaryResource: boolean;
          parentResId: any;
        }) => {
          if (
            resource.isSecondaryResource &&
            resource.resource_id === resourceData?.resource_id &&
            resource.parentResId === resourceData?.parentResId
          ) {
            return newResourceData;
          }

          if (
            // !resource.isSecondaryResource &&
            resource?.resource_id === resourceData?.resource_id
          ) {
            return newResourceData;
          }

          return resource;
        }
      );

      setResourcesData(updatedResourcesData);
    }
    // setIsEditResource(false);
    setIsResourceEdit("");
    if (formData?.isUpdateResource) {
      updateResourceData(newResourceData);
      setComment("");
    }
    if (isFromQueryExecution) {
      setViewInlineVariables((prev: any) => ({
        ...prev,
        used_generic_research_query_variables: globalVariables,
      }));
    } else {
      setViewInlineVariables((prev: { resource_variables: any[] }) => {
        const resourceToUpdate = prev?.resource_variables?.find(
          (item: { resource_id: any }) =>
            item?.resource_id === resourceData?.resource_id
        );
        const updatedResource = {
          ...resourceToUpdate,
          resource_vars: globalVariables,
        };

        const newArr = JSON.parse(JSON.stringify(prev?.resource_variables));
        return {
          ...prev,
          resource_variables: newArr
            ? newArr.map((item: { resource_id: any }) =>
                item?.resource_id === resourceToUpdate?.resource_id
                  ? { ...updatedResource }
                  : item
              )
            : [...prev?.resource_variables, updatedResource],
        };
      });
    }
  };
  const handleCancelResource = () => {
    setOpenResourceConfirmation(false);
    setComment("");
  };
  const handleSaveResource = () => {
    setOpenResourceConfirmation(false);
    onSaveResource();
  };
  const handleCommentChange = (e: any) => {
    setComment(e.target.value);
  };
  const saveResource = async () => {
    try {
      const type = formData?.linked_service?.sub_type;
      let uniqueBaseResourceColumns;
      if (baseResourceColumns && baseResourceColumns.length > 0) {
        const uniqueColumns = new Set(
          baseResourceColumns.map((column: { label: any }) => column.label)
        );
        uniqueBaseResourceColumns = Array.from(uniqueColumns);
      } else {
        uniqueBaseResourceColumns = null;
      }
      if (formData.aggregation_type === "flat") {
        if (type === "blob") {
          await reRunBlobLinkedServiceSchema.validate(formData, {
            abortEarly: false,
          });
        } else if (type === "sftp") {
          await reRunSftpLinkedServiceSchema.validate(formData, {
            abortEarly: false,
          });
        } else if (type === "local") {
          await reRunLocalLinkedServiceSchema.validate(formData, {
            abortEarly: false,
          });
        } else if (sqlDatabaseType.includes(type)) {
          await reRunSqlLinkedServiceSchema.validate(formData, {
            abortEarly: false,
          });
        } else if (type === "basic_auth_api") {
          await reRunBasicAuthApiDefinitionSchema.validate(formData, {
            abortEarly: false,
          });
        } else if (type === "token_auth_api") {
          await reRunTokenAuthApiDefinitionSchema.validate(formData, {
            abortEarly: false,
          });
        } else if (type === "key_auth_api") {
          await reRunKeyAuthApiDefinitionSchema.validate(formData, {
            abortEarly: false,
          });
        } else if (type === "oauth_api") {
          await reRunOAuthApiDefinitionSchema.validate(formData, {
            abortEarly: false,
          });
        } else if (type === "no_auth_api") {
          await reRunNoAuthApiDefinitionSchema.validate(formData, {
            abortEarly: false,
          });
        }
      } else {
        const updatedFormData = {
          ...formData,
          base_resource_columns: uniqueBaseResourceColumns,
        };
        await aggregatedValidateSchema.validate(updatedFormData, {
          abortEarly: false,
        });
      }
      if (formData?.isUpdateResource) {
        setOpenResourceConfirmation(true);
      } else {
        onSaveResource();
        setIsResourceEdit("");
      }
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      if (validationErrors.inner) {
        validationErrors.inner.forEach(
          (error: { path: string | number; message: string }) => {
            const fieldName = String(error.path).replace(
              /^resource_definition(\.api_definition)?\./,
              ""
            );
            newErrors[fieldName] = error.message;
          }
        );
      }
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };
  const updateResourceData = (resourceData: any) => {
    const resourceId = resourceData?.resource_id;
    let definitionType = resourceData?.type + "_definition";
    if (sqlDatabaseType.includes(resourceData?.type)) {
      definitionType = "sql_definition";
    }
    const reqBody: any = {
      resource_name: resourceData.resource_name,
      system_id: resourceData.system_id || resourceDetailData.system_id,
      resource_type: resourceDetailData.resource_type,
      code: resourceDetailData?.code,
      resource_prefix: resourceData.resource_prefix,
      aggregation_type: resourceData.aggregation_type,
      domain_id: resourceDetailData?.domain_id,
      domain_name: resourceDetailData?.domain_name,
      domain_code: resourceDetailData?.domain_code,
      is_active: resourceData.is_active,
      linked_service_id: resourceData.linked_service_id,
      linked_service_code: resourceData.linked_service_code,
      current_url: resourceData.current_url,
      file_processing_id: resourceData.file_processing_id,
      file_processing_code: resourceData.file_processing_code,
      comment: comment,
      additional_properties: {
        resource_definition:
          resourceData.aggregation_type === "flat"
            ? {
                type: resourceData?.type,
                [definitionType]: {
                  resource_path: resourceData?.resource_path,
                  file_name: resourceData?.file_name,
                  column_delimiter: resourceData?.column_delimiter,
                  connection_key: resourceData?.connection_key,
                  connection_key_code: connectionKeysData?.find(
                    (option: { id: any }) =>
                      option.id === formData?.connection_key
                  )?.code,
                  container_name: resourceData?.container_name,
                  database_type: resourceData?.database_type,
                  connection_string: resourceData?.connection_string,
                  sql_query: resourceData?.sql_query,
                  remote_directory: resourceData?.remote_directory,
                  use_multi_thread_reader:
                    resourceData?.use_multi_thread_reader ||
                    resourceData?.additional_properties?.resource_definition
                      ?.sql_definition?.use_multi_thread_reader ||
                    false,
                  column_name_to_partition_on_sql_query:
                    resourceData?.column_name_to_partition_on_sql_query ||
                    resourceData?.additional_properties?.resource_definition
                      ?.sql_definition?.column_name_to_partition_on_sql_query ||
                    "",
                  use_parquet_export: resourceData?.use_parquet_export || false,
                  parquet_export_definition: resourceData?.use_parquet_export
                    ? resourceData?.parquet_export_definition || null
                    : null,
                },
                // api_definition:
                //   resourceData
                //     ?.api_definition,
                api_definition: apiType.includes(resourceData?.type)
                  ? {
                      ...resourceData?.api_definition,
                      query_params: queryParams,
                      url_params: urlParams,
                      body:
                        resourceData?.api_definition?.method !== "get"
                          ? resourceData?.api_definition?.body
                          : null,
                    }
                  : null,
              }
            : null,
        aggregation_properties:
          resourceData.aggregation_type === "aggregated"
            ? resourceData?.aggregation_properties
            : null,
        resource_column_details_id: resourceData?.resource_column_details_id,
        additional_resource_data: resourceData?.additional_resource_data,
        filter_rules: resourceData?.filter_rules,
        inline_variables: resourceData?.inline_variables,
        resource_column_details_code:
          resourceDetailData?.additional_properties
            ?.resource_column_details_code || null,
      },
    };
    if (formData?.linked_service?.type === "sql") {
      const keysToRemove = [
        "file_processing_code",
        "file_processing_id",
        "column_delimiter",
        "container_name",
        "file_name",
        "resource_path",
        "database_type",
        "connection_string",
      ];
      keysToRemove.forEach((key) => {
        if (key in reqBody) {
          delete reqBody[key];
        } else if (
          key in
          reqBody.additional_properties?.resource_definition?.sql_definition
        ) {
          delete reqBody.additional_properties.resource_definition
            .sql_definition[key];
        }
      });
    }
    const resourceObject = removeUndefinedProperties(reqBody);
    const { resource_tags_details, ...restObject } = resourceObject;
    const finalResourceObject = {
      resource: { ...restObject },
      resource_tags: authTagDetails,
    };
    updateResource({
      currentResourceId: resourceId,
      payload: finalResourceObject,
    })
      .then((response: any) => {
        if (response) showToast("Resource updated successfully!", "success");
      })
      .catch((error: any) => {
        showToast(`Cannot update resource`, "error");
      });
  };

  return (
    <Box sx={{ marginBottom: "16px" }}>
      <Grid container rowSpacing={3} columnSpacing={3}>
        <ReRunLinkedServices
          formData={formData}
          setFormData={setFormData}
          connectionKeysData={connectionKeysData}
          linkedServicesData={linkedServicesData}
          setResourceId={setResourceId}
          resourceId={resourceId}
          setIsLoading={setIsLoading}
          setSelectSystemId={setSelectSystemId}
          setAuthTagDetails={setAuthTagDetails}
          fileStreamIndex={fileStreamIndex}
          errors={errors}
          setErrors={setErrors}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          urlParams={urlParams}
          setUrlParams={setUrlParams}
          type={type}
          setType={setType}
          setAllVariablesList={setAllVariablesList}
          allVariablesList={allVariablesList}
          resourceColumnData={resourceColumnData}
          setCancelUploadFile={setCancelUploadFile}
          cancelUploadFile={cancelUploadFile}
          setIsLoadingFile={setIsLoadingFile}
          isLoadingFile={isLoadingFile}
          setHasVariableChanges={setHasVariableChanges}
        />
        {(formData?.linked_service?.type === "sql" ||
          formData?.linked_service?.type === "postgres_sql") && (
          <>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="base-resource-checkbox m-0">
                <Checkbox
                  checked={formData?.use_multi_thread_reader || false}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData({
                      ...formData,
                      use_multi_thread_reader: event.target.checked,
                      column_name_to_partition_on_sql_query: event.target
                        .checked
                        ? formData?.column_name_to_partition_on_sql_query || ""
                        : null,
                    });
                  }}
                  sx={{
                    "&.Mui-checked": {
                      color: "#FFA500",
                    },
                  }}
                />
                <span>Use Multi Thread Reader</span>
              </label>
            </Grid>

            {formData?.use_multi_thread_reader && (
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <TextField
                  type="text"
                  title="Column Name to Partition On"
                  name="column_name_to_partition_on_sql_query"
                  fullWidth
                  label={
                    <span>
                      Column Name to Partition On
                      <span className="required-asterisk">*</span>
                    </span>
                  }
                  variant="outlined"
                  className={`form-control-autocomplete ${
                    errors?.column_name_to_partition_on_sql_query
                      ? "has-error"
                      : ""
                  }`}
                  value={
                    formData?.column_name_to_partition_on_sql_query || null
                  }
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      column_name_to_partition_on_sql_query: e.target.value,
                    });
                  }}
                  error={!!errors?.column_name_to_partition_on_sql_query}
                  helperText={
                    errors?.column_name_to_partition_on_sql_query || ""
                  }
                />
              </Grid>
            )}
          </>
        )}
        <Grid
          item
          xs
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: "8px",
              flexWrap: "wrap",
              rowGap: "16px",
            }}
          >
            {canEditResource && (
              <label className="base-resource-checkbox m-0">
                <Checkbox
                  checked={formData.isUpdateResource || false}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData({
                      ...formData,
                      isUpdateResource: event.target.checked,
                    });
                    if (event.target.checked)
                      setCurrentResourceId(
                        formData?.id || formData?.resource_id
                      );
                  }}
                  sx={{
                    "&.Mui-checked": {
                      color: "#FFA500",
                    },
                  }}
                />
                <span>Update Resource</span>
              </label>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: "8px",
                flexWrap: "wrap",
              }}
            >
              <Button
                color="secondary"
                variant="contained"
                onClick={() => {
                  // setIsEditResource(false);
                  setIsResourceEdit("");
                  if (cancelUploadFile) {
                    cancelUploadFile.cancel();
                  }
                }}
                className="btn-orange btn-dark"
              >
                Cancel
              </Button>
              <Button
                color="secondary"
                variant="contained"
                onClick={() => saveResource()}
                className="btn-orange"
                disabled={!hasChanges}
              >
                <SaveOutlinedIcon /> &nbsp; Save
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <ConfirmationDialog
        title={"Confirm updating Resource"}
        dialogContent={
          <>
            <p>
              Confirm changes before saving them for the
              {isFromRuleExecution ? " execution" : " validation"} step.
              {formData.isUpdateResource
                ? "This action will also update the Resource !"
                : ""}
            </p>
            <div>
              <p className="m-0 mb-2">
                Add a note before updating the Resource
              </p>
              <textarea
                value={comment}
                className={`form-control-1 max-60`}
                onChange={(e) => handleCommentChange(e)}
              />
            </div>
          </>
        }
        handleCancel={handleCancelResource}
        openConfirmation={openResourceConfirmation}
        handleConfirm={handleSaveResource}
      />
    </Box>
  );
};
export default ReRunEditRuleExecutionTab;
